import { useRouter } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { keys, queryClient } from '../lib/api';
import { useAuth } from '../lib/auth';
import { accessOf, ended, outOfDate } from '../lib/billing';
import { useMe } from '../lib/queries';
import { needsWelcome } from '../lib/settings';

// Only a reader who is new answers the welcome questions, so they load on demand.
const Start = lazy(() => import('./Start').then((m) => ({ default: m.Start })));
// And only one past their free days and paid months is asked to pay.
const Paywall = lazy(() => import('./Payment').then((m) => ({ default: m.Paywall })));

// When this visit began. Settings restored from this device's cache are older.
const opened = Date.now();

// The longest a browser's timer waits: a little under 25 days.
const LONGEST_TIMER = 2 ** 31 - 1;

/**
 * What a signed-in reader is shown: the way to pay, the welcome questions,
 * the app, or nothing yet.
 *
 * Paying comes first: where wellread asks to be paid, a reader past their
 * free days and paid months has the way to pay in place of the app. That
 * too is on the server's word. A cached copy may predate a payment made on
 * another device, so one that says the reader is past both waits for the
 * fetch on its way; and one that says they may read, with an end that has
 * since passed, is out of date and is asked for again (Welcome does the
 * asking). With nobody to ask, the copy held here stands.
 *
 * The questions are asked only on the server's word, heard this visit. A
 * cached copy that says the reader is new may predate their answers (the
 * cache is written on a throttle), so it waits for the fetch that is already
 * on its way. With no settings at all it waits too, so the app never shows
 * for a moment before the questions do. Whenever the server can't be asked
 * (no connection, or the settings failed to load) the app opens as it always
 * has, and its own screens say what is wrong. Once the settings have failed
 * it never waits again: a query with nothing in it reads as pending while it
 * is asked again, and waiting would take the app away and bring it back,
 * asking again each time.
 */
function decide(me: ReturnType<typeof useMe>): 'pay' | 'ask' | 'wait' | 'open' {
  if (!me.data) return me.isPending && me.fetchStatus !== 'paused' && !me.errorUpdatedAt ? 'wait' : 'open';
  const access = accessOf(me.data);
  if (ended(access)) return me.dataUpdatedAt < opened && me.isFetching ? 'wait' : 'pay';
  if (outOfDate(access, me.dataUpdatedAt)) return me.isFetching ? 'wait' : 'pay';
  if (!needsWelcome(me.data)) return 'open';
  if (me.dataUpdatedAt >= opened) return 'ask';
  return me.isFetching ? 'wait' : 'open';
}

/**
 * Stands in front of every signed-in layout, whatever address the reader
 * arrived at. If the session ends while they're here, they sign in again and
 * come back to the same place.
 */
export function Gate({ children }: { children: ReactNode }) {
  const signedOut = useAuth().status === 'signed-out';
  const router = useRouter();
  // The routes' own guard sends a signed-out reader to sign in, and back here afterwards.
  useEffect(() => {
    if (signedOut) void router.invalidate();
  }, [signedOut, router]);
  if (signedOut) return null;
  return <Welcome>{children}</Welcome>;
}

/** The way to pay, the welcome questions, the app, or nothing yet. */
function Welcome({ children }: { children: ReactNode }) {
  const me = useMe();
  const state = decide(me);
  // The copy held here says the reader may read until a time that has passed: ask again.
  const stale = !me.isFetching && outOfDate(accessOf(me.data), me.dataUpdatedAt);
  useEffect(() => {
    if (stale) void queryClient.invalidateQueries({ queryKey: keys.me });
  }, [stale]);
  // And the moment what they have runs out with the app open, so the way to
  // pay takes its place then, not at whatever they next ask the server for.
  const until = accessOf(me.data)?.until;
  const heard = me.dataUpdatedAt;
  useEffect(() => {
    if (!until) return;
    const left = Date.parse(until) - Date.now();
    if (left <= 0) return;
    const timer = setTimeout(
      () => void queryClient.invalidateQueries({ queryKey: keys.me }),
      Math.min(left + 1000, LONGEST_TIMER),
    );
    return () => clearTimeout(timer);
  }, [until, heard]);
  if (state === 'open') return children;
  return (
    <main className="start">
      <Suspense fallback={<Waiting />}>
        {state === 'pay' ? <Paywall /> : state === 'ask' ? <Start /> : <Waiting />}
      </Suspense>
    </main>
  );
}

function Waiting() {
  return (
    <p className="note loading start-wait" role="status">
      Opening your library
    </p>
  );
}
